import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import type { PaymentDoc } from '@flintmint/core-shared/firestore/schemas';
import { getAuthUser } from '@flintmint/web-shared/hooks/use-auth-user';

import { useServices } from '@/providers/services-provider';

/**
 * The signed-in user's payments with their document ids, newest `paidAt` first, live while mounted; undefined until
 * loaded or after a listener error. Sorted here, so the single `userId` filter needs no composite index.
 */
export const usePayments = () => {
  const { firestore } = useServices();
  const { id: userId } = getAuthUser();
  const [payments, setPayments] = useState<(PaymentDoc & { id: string })[]>();

  useEffect(() => {
    if (!firestore || !userId) return;

    const unsubscribe = onSnapshot(
      query(collection(firestore, 'payments'), where('userId', '==', userId)),
      (snapshot) =>
        setPayments(
          snapshot.docs
            .map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as PaymentDoc) }))
            .sort((a, b) => b.paidAt.localeCompare(a.paidAt))
        ),
      (error) => {
        Sentry.captureException(error);
        setPayments(undefined);
      }
    );
    return () => {
      unsubscribe();
      setPayments(undefined);
    };
  }, [firestore, userId]);

  return payments;
};
